<template>
  <div v-clickOutside="close" class="relative no-drag">
    <div ref="referenceRef" class="flex" @click="toggle">
      <slot name="button" :isOpen="isDropdownVisible">
        <ButtonModule
          :text="text"
          :prefix="icon"
          :suffix="suffix"
          :has-hover="!isDropdownVisible && iconHasHover"
          class="text-app-color px-0 py-0"
          :classes="`${classes} ${buttonClasses}`"
          :button-opacity="buttonOpacity"
        />
      </slot>
    </div>

    <!-- A P P E N D  T O  B O D Y -->
    <div
      v-if="appendToBody && isDropdownVisible"
      ref="contentRef"
      class="dropdown rounded-10 shadow-dropdown"
      :class="[dropdownZIndex, dropdownMenuPosition]"
      :style="`top: ${topPosition}; ${menuPosition}: 0; width: ${width}px`"
    >
      <div class="rounded-10" :class="background" @click="close">
        <slot />
      </div>
    </div>

    <!-- D O E S N ' T   A P P E N D  T O  B O D Y -->
    <teleport
      v-if="!appendToBody"
      to="body"
    >
      <div
        v-show="isDropdownVisible "
        ref="content"
        class="dropdown rounded-10 shadow-dropdown"
        :class="[dropdownZIndex, dropdownMenuPosition]"
        :style="`top: ${position.top}px; left: ${positionLeft || position.left}px; width: ${width}${width === 'auto' ? '' : 'px'}`"
      >
        <div class="rounded-10" :class="background" @click="close">
          <slot />
        </div>
      </div>
    </teleport>
  </div>
</template>

<script>
import ButtonModule from '@/components/shared/form-controls/ButtonModule'
import { useStore } from 'vuex'
import { defineComponent, ref, computed, reactive } from 'vue'

export default defineComponent({
  name: 'DropdownModule',

  components: { ButtonModule },

  props: {
    customClasses: String,
    buttonClasses: { type: String, default: 'px-5 py-3' },
    text: { type: String, required: false },
    icon: { type: String, required: false },
    suffix: { type: String, required: false },
    width: { type: Number, default: 225 },
    iconHasHover: { type: Boolean, default: true },
    background: { type: String },
    contentHeight: { type: [Number, Boolean], default: false },
    dropdownZIndex: { type: String, default: 'z-50' },
    dropdownMenuPosition: { type: String, default: 'absolute' },
    positionLeft: { type: String, default: '' },
    removeOptionalTopGap: { type: Boolean, default: false },
    buttonOpacity: { type: String },
    optionalLeft: { type: Number, default: 0 },
    appendToBody: { type: Boolean, default: false },
    imageWidth: { type: Number, default: 0 },
    positionMenu: { type: String, default: null },
    isAutoMenuPosition: { type: Boolean, default: null },
    topPosition: { type: String, default: '38px' },
    disabled: Boolean
  },

  emits: ['close', 'input'],

  setup (props, { emit }) {
    const { state, getters } = useStore()

    const isDropdownVisible = ref(false)
    const referenceRef = ref(null)
    const contentRef = ref(null)

    const position = reactive({
      top: 0,
      left: 0
    })

    const showSidebar = computed(() => state.settings.showSidebar)
    const isMobile = computed(() => getters.isMobile)
    const isDarkMode = computed(() => getters.isDarkMode)

    const menuPosition = computed(() => {
      const contentRect = contentRef.value?.getBoundingClientRect()
      const position = isMobile.value && props.imageWidth <= 180 && props.imageWidth !== 0 ? 'left' : 'right'
      if (props.isAutoMenuPosition) {
        return contentRect?.left < 0 ? 'left' : 'right'
      } else {
        return props.positionMenu ? props.positionMenu : position
      }
    })

    const classes = computed(() => {
      if (props.customClasses) return props.customClasses

      const prefix = isDropdownVisible.value ? '' : 'hover:'
      return `${isDarkMode.value ? `${prefix}bg-dark-500` : `${prefix}bg-menu-hover`} rounded-8 anim-color-200`
    })

    function toggle () {
      if (props.disabled) return
      isDropdownVisible.value = !isDropdownVisible.value
      emit('input', isDropdownVisible)

      if (isDropdownVisible.value) {
        setPosition()
      }
    }

    function close () {
      emit('close')
      isDropdownVisible.value = false
    }

    function setPosition () {
      const rect = referenceRef.value.getBoundingClientRect()
      const topGap = props.removeOptionalTopGap ? 0 : 10

      if (props.contentHeight && window.innerHeight < rect.bottom + topGap + props.contentHeight) {
        position.top = rect.top - props.contentHeight - topGap
        position.left = rect.left + rect.width - props.width
      } else {
        position.top = rect.bottom + topGap
        position.left = rect.left + rect.width - props.width - props.optionalLeft
      }

      if (position.left < 10) {
        position.left = 10
      }
    }

    return {
      // data
      isDropdownVisible,
      position,
      // computed
      showSidebar,
      isMobile,
      menuPosition,
      classes,
      // refs
      referenceRef,
      contentRef,
      // functions
      toggle,
      close,
      setPosition
    }
  }
})
</script>

<style scoped lang="scss">
.dropdown__arrow {
  position: absolute;
  width: 25px;
  height: 26px;
  right: 10px;
  transform: rotate(45deg);
  z-index: -1;
  box-shadow: 2.5px 2.5px 8.5px rgba(0, 0, 0, 0.2);
}
</style>
